import { APPEARANCE_KEY, DARK_MODE_KEY } from '@backoffice-frontend/common'
import { useLocalStorage } from '@backoffice-frontend/hooks'
import { useEffect } from 'react'

export enum AppearanceOptions {
  system = 'system',
  dark = 'dark',
  light = 'light',
}

/**
 * Custom hook for managing appearance and dark mode settings.
 *
 * @returns {boolean} darkMode - A boolean indicating if dark mode is enabled.
 * @returns {AppearanceOptions} appearanceOption - An object containing the appearance settings.
 * @private {Function} setDarkMode - Private API, do not use.
 * @private {Function} setAppearanceOption - Private API, do not use.
 */
export const useAppearance = () => {
  const [appearanceOption, setAppearanceOption] =
    useLocalStorage<AppearanceOptions>(APPEARANCE_KEY, AppearanceOptions.light)
  const [darkMode, setDarkMode] = useLocalStorage<boolean>(DARK_MODE_KEY, false)

  useEffect(() => {
    if (appearanceOption !== AppearanceOptions.system) return

    const colorScheme = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = ({
      matches,
    }: MediaQueryListEvent | MediaQueryList) => {
      setDarkMode(matches)
    }

    colorScheme.addEventListener('change', handleChange)

    // Sync now: the OS preference may have changed while the app was closed, or while
    // an explicit light/dark option was selected.
    handleChange(colorScheme)

    return () => {
      colorScheme.removeEventListener('change', handleChange)
    }
  }, [appearanceOption, setDarkMode])

  return { appearanceOption, setAppearanceOption, darkMode, setDarkMode }
}
